import { Box, Button, Divider, Group, IconArrowLeft, Loader, Token, Typography, Wrapper } from '@screentone/core';

import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AuraEmployeeResponse, useAuraQuery } from 'hooks/useAuraQuery';
import { formatInTimeZone } from 'utils/dates';
import { EditorLayout } from 'utils/layoutUtils';
import { HistoryModuleItem } from './HistoryModuleItem';

export interface HistoryEntryProps {
  createdUtc: number;
  djUserId?: string;
  layout: EditorLayout;
}

export const HistoryEntry = ({ createdUtc, djUserId, layout }: HistoryEntryProps) => {
  const { restoreFromHistory } = useStructureEditContext();
  const currentProperty = useConvertedProperty();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { data: userData, isLoading: isFetchingUser } = useAuraQuery(djUserId!, {
    currentProperty,
    enabled: !!djUserId
  });
  const userTyped = userData as AuraEmployeeResponse | undefined;
  const userText = userTyped ? `${userTyped.first_name ?? ''} ${userTyped.last_name ?? ''}` : djUserId;

  const tz = publicationSettingsResp?.publicationSetting.preferredTimezone;
  const dateText = formatInTimeZone(createdUtc, tz, 'MMM d, yyyy h:mm aaa', { showTimezoneAbbreviation: true });
  const groups = layout.structure?.groups ?? [];
  const isEmpty = !groups.some((g) => g.modules.length > 0);

  return (
    <Box>
      <Wrapper padding={{ all: 'md' }}>
        <Group gap="sm" valign="center" margin={{ bottom: 'sm' }}>
          <Typography variant="header3" margin={{ bottom: 'none' }}>
            Previous
          </Typography>
          <Typography variant="note">{dateText}</Typography>
        </Group>
        <Button tertiary icon={IconArrowLeft} onClick={() => restoreFromHistory(layout)}>
          Replace draft
        </Button>
      </Wrapper>
      <Divider />
      <Wrapper padding={{ all: 'md' }}>
        {groups.map((group) => (
          <div key={group.id}>
            {group.config.label && (
              <Typography variant="label2" size="sm" color="asphalt" weight="bold" margin={{ bottom: 'xs' }}>
                {group.config.label.toUpperCase()}
                {group.config.showModuleCount && (
                  <Token margin={{ bottom: 'none', left: 'xs' }}>
                    {`${group.modules.length} ${group.modules.length === 1 ? 'module' : 'modules'}`}
                  </Token>
                )}
              </Typography>
            )}
            {group.modules.map((module, idx) => (
              <HistoryModuleItem key={idx} module={module} />
            ))}
          </div>
        ))}
        {isEmpty && (
          <Typography variant="note" color="asphalt">
            No modules in this version
          </Typography>
        )}
      </Wrapper>
      <Divider />
      <Wrapper padding={{ all: 'md' }}>
        <Typography variant="note" color="asphalt">
          Published by: {isFetchingUser ? <Loader size="md" /> : (userText ?? '—')}
        </Typography>
      </Wrapper>
    </Box>
  );
};
